/* ==========================================================================
   sd-2026.js - the behaviours this build added
   --------------------------------------------------------------------------
   Every one of these is an ENHANCEMENT. Each checks that the thing it is about
   to upgrade actually exists, and each leaves a working page behind if it is
   never reached: the tabs start as stacked headed sections, the rails are
   native scroll containers, quick add is a button that falls back to the
   product page, the cart has a real Update button inside <noscript>, and
   recently viewed simply does not appear.

   Nothing in here hides content that has no other way of being shown. The one
   component that hides anything - the tab panels - only starts hiding after it
   has built the tablist that reveals them again.
   ========================================================================== */

(function () {
  'use strict';

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function toast(msg) {
    if (typeof window.showToast === 'function') { window.showToast(msg); return; }
    var el = document.getElementById('toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('show');
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.classList.remove('show'); }, 2800);
  }

  /* ---------------------------------------------------------------------
     1. Header: a scrolled state, and the cart badge
     --------------------------------------------------------------------- */
  function initHeader() {
    var header = $('[data-sd-header]');
    if (!header) return;

    var ticking = false;
    function apply() {
      if (window.scrollY > 8) {
        header.setAttribute('data-scrolled', '');
      } else {
        header.removeAttribute('data-scrolled');
      }
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }, { passive: true });
    apply();
  }

  // theme.js has an updateCartCount, but it looks for `.header-cart-count`,
  // which this header does not render. Rather than edit a file three other
  // templates depend on, this keeps the new badge in sync itself.
  function syncCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        $$('[data-sd-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) { el.removeAttribute('hidden'); } else { el.setAttribute('hidden', ''); }
        });
        return cart;
      })
      .catch(function () { /* the badge is cosmetic; a failure is not worth a message */ });
  }

  /* ---------------------------------------------------------------------
     2. Quick add
     ---------------------------------------------------------------------
     A single-variant product is added in place. A multi-variant product is
     never guessed at: its button says "View options" and navigates, so the
     control cannot do something other than what it says. Any failure sends the
     visitor to the product page, which is always a correct outcome. */
  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-sd-quick-add]') : null;
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      var url = btn.getAttribute('data-product-url');
      var title = btn.getAttribute('data-product-title') || 'Item';
      var id = btn.getAttribute('data-variant-id');

      if (btn.getAttribute('data-needs-options') === 'true' || !id) {
        if (url) window.location.href = url;
        return;
      }

      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add failed');
          return res.json();
        })
        .then(function () {
          btn.textContent = 'Added';
          toast(title + ' added to cart');
          return syncCart();
        })
        .then(function () {
          if (typeof window.openAmpDrawer === 'function') window.openAmpDrawer();
        })
        .catch(function () { if (url) window.location.href = url; })
        .finally(function () {
          setTimeout(function () {
            btn.disabled = false;
            btn.textContent = label;
          }, 1400);
        });
    });
  }

  /* ---------------------------------------------------------------------
     3. Tabs
     ---------------------------------------------------------------------
     Progressive by construction. The markup ships as a stack of headed
     sections with every panel visible. This builds the tablist, marks the
     container ready (which is what makes the CSS start hiding panels), and
     wires roving tabindex plus Home/End/arrow keys. If this never runs the
     product description, specifications and shipping terms are all on the page
     with their own headings - which is the whole point, because the previous
     build shipped a product page whose description was destroyed by an app and
     rendered as a heading with nothing under it. */
  function initTabs() {
    $$('[data-sd-tabs]').forEach(function (root) {
      var panels = $$('[data-sd-tab-panel]', root);
      if (panels.length < 2) return;

      var list = $('[data-sd-tab-list]', root);
      if (!list) return;

      var tabs = [];

      panels.forEach(function (panel, i) {
        var id = panel.id || 'sd-panel-' + i;
        panel.id = id;
        var label = panel.getAttribute('data-sd-tab-label') || ('Section ' + (i + 1));

        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sd-tabs__tab';
        btn.id = id + '-tab';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-controls', id);
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        btn.tabIndex = i === 0 ? 0 : -1;
        btn.textContent = label;
        li.appendChild(btn);
        list.appendChild(li);
        tabs.push(btn);

        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', btn.id);
        panel.tabIndex = 0;
        if (i !== 0) panel.hidden = true;
      });

      list.setAttribute('role', 'tablist');

      function select(i) {
        tabs.forEach(function (t, j) {
          var on = i === j;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          panels[j].hidden = !on;
        });
        tabs[i].focus();
      }

      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { select(i); });
        t.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
          if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = tabs.length - 1;
          if (next === null) return;
          e.preventDefault();
          select(next);
        });
      });

      root.setAttribute('data-sd-tabs-ready', '');
    });
  }

  /* ---------------------------------------------------------------------
     4. Rails
     ---------------------------------------------------------------------
     The track is a native horizontal scroll container with snapping and is
     fully usable before this runs. The arrows start hidden and are revealed
     only when the content genuinely overflows, so they never appear as dead
     controls beside a rail with two items in it. */
  function initRails() {
    $$('[data-sd-rail]').forEach(function (rail) {
      if (rail._sdRailed) return;
      rail._sdRailed = true;

      var track = $('[data-sd-rail-track]', rail);
      var nav = $('[data-sd-rail-nav]', rail);
      var prev = $('[data-sd-rail-prev]', rail);
      var next = $('[data-sd-rail-next]', rail);
      if (!track || !nav || !prev || !next) return;

      function sync() {
        if (track.scrollWidth - track.clientWidth <= 8) { nav.hidden = true; return; }
        nav.hidden = false;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      }

      function step(dir) {
        var item = track.querySelector('.sd-rail__item');
        var amount = item ? item.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
        track.scrollBy({ left: dir * amount, behavior: 'smooth' });
      }

      prev.addEventListener('click', function () { step(-1); });
      next.addEventListener('click', function () { step(1); });
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  /* ---------------------------------------------------------------------
     5. Recently viewed
     ---------------------------------------------------------------------
     THE PRICE STRING IS COPIED, NEVER COMPUTED. What gets stored is the exact
     text sd-price.liquid already rendered on the product page, which means the
     MAP guard travels with it: a product priced below its custom.map_price is
     stored, and redisplayed, as the withheld label. There is no code path in
     this file that turns a number into money, because that would be a way to
     advertise the figure the guard exists to suppress.

     Storage is per browser and never leaves it. Every read and write is
     wrapped, because private mode and blocked site data both throw. */
  var RV_KEY = 'sdp:recently-viewed';
  var RV_MAX = 8;

  function rvRead() {
    try {
      var raw = window.localStorage.getItem(RV_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) { return []; }
  }

  function rvWrite(list) {
    try { window.localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX))); } catch (e) { /* nothing to do */ }
  }

  function initRecentlyViewed() {
    var src = $('[data-sd-rv-source]');
    if (src) {
      var item = {
        url: src.getAttribute('data-url') || '',
        title: src.getAttribute('data-title') || '',
        brand: src.getAttribute('data-brand') || '',
        img: src.getAttribute('data-img') || '',
        price: src.getAttribute('data-price') || ''
      };
      if (item.url && item.title) {
        var list = rvRead().filter(function (p) { return p.url !== item.url; });
        list.unshift(item);
        rvWrite(list);
      }
    }

    var mount = $('[data-sd-rv-mount]');
    if (!mount) return;

    var here = mount.getAttribute('data-current-url') || '';
    var items = rvRead().filter(function (p) { return p.url !== here; }).slice(0, 6);
    if (items.length < 2) return;

    var track = $('[data-sd-rail-track]', mount);
    if (!track) return;

    items.forEach(function (p) {
      var li = document.createElement('li');
      li.className = 'sd-rail__item';

      var card = document.createElement('article');
      card.className = 'sd-card sd-card--sm';

      var media = document.createElement('div');
      media.className = 'sd-card__media';
      if (p.img) {
        var im = document.createElement('img');
        im.className = 'sd-card__img';
        im.src = p.img;
        im.alt = '';
        im.loading = 'lazy';
        media.appendChild(im);
      }

      var body = document.createElement('div');
      body.className = 'sd-card__body';

      if (p.brand) {
        var b = document.createElement('p');
        b.className = 'sd-card__brand';
        b.textContent = p.brand;
        body.appendChild(b);
      }

      var h = document.createElement('h3');
      h.className = 'sd-card__title';
      var a = document.createElement('a');
      a.className = 'sd-card__link';
      a.href = p.url;
      a.textContent = p.title;
      h.appendChild(a);
      body.appendChild(h);

      if (p.price) {
        var foot = document.createElement('div');
        foot.className = 'sd-card__foot';
        var pr = document.createElement('p');
        pr.className = 'sd-price sd-price--sm';
        pr.textContent = p.price;
        foot.appendChild(pr);
        body.appendChild(foot);
      }

      card.appendChild(media);
      card.appendChild(body);
      li.appendChild(card);
      track.appendChild(li);
    });

    mount.hidden = false;
    initRails();
  }

  /* ---------------------------------------------------------------------
     6. Quantity steppers
     --------------------------------------------------------------------- */
  function initQty() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-sd-qty]') : null;
      if (!btn) return;
      var wrap = btn.closest('[data-sd-qty-wrap]');
      if (!wrap) return;
      var input = $('input', wrap);
      if (!input) return;
      var delta = btn.getAttribute('data-sd-qty') === 'up' ? 1 : -1;
      var min = parseInt(input.getAttribute('min') || '1', 10);
      var val = Math.max(min, (parseInt(input.value, 10) || min) + delta);
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------------------------------------------------------------------
     7. Forms that submit themselves
     ---------------------------------------------------------------------
     Used by the collection sort control and by the cart. DEBOUNCED at 400ms
     on purpose: a shopper clicking the plus button three times would otherwise
     fire three navigations, and the second would cancel the first before its
     update landed - which loses a quantity change on the checkout path.

     requestSubmit() is called with no submitter, so no button name is sent.
     That is exactly right for the cart: the POST carries updates[] and gets an
     update, not a jump to checkout. Both forms keep a real submit button
     inside <noscript>. */
  function initAutoSubmit() {
    $$('[data-sd-autosubmit]').forEach(function (form) {
      if (form.tagName !== 'FORM') form = form.querySelector('form') || form;
      if (form.tagName !== 'FORM') return;

      var timer = null;
      form.addEventListener('change', function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.submit();
          }
        }, 400);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */

  /* ------------------------------------------------------------------
     CART MUTATION WATCH

     Replaces theme.js initCartSync, which polled /cart.js forever. The rule that
     kills the loop: only a request that CHANGES the cart may trigger a re-read, and
     the re-read itself is issued through the ORIGINAL fetch, so it cannot be seen by
     this wrapper even if the endpoint test were ever loosened.

     This is what keeps the header badge honest when the cart is mutated by code this
     theme does not own - the Slide Cart drawer app removing a line item, a quantity
     stepper inside it, or any other app posting to /cart/change.js. Tim reported
     exactly that: remove an item in the drawer, header still says 1.
  ------------------------------------------------------------------ */
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function isMutation(url) {
    try { return CART_MUTATION.test(String(url)); } catch (e) { return false; }
  }

  function initCartWatch() {
    var nativeFetch = window.fetch;

    if (nativeFetch && !nativeFetch.__sdpWrapped) {
      var wrapped = function () {
        var a = arguments[0];
        var url = (typeof a === 'string') ? a : ((a && a.url) || '');
        var p = nativeFetch.apply(this, arguments);
        if (isMutation(url) && p && typeof p.then === 'function') {
          p.then(function (res) { setTimeout(syncCart, 250); return res; })
           .catch(function () {});
        }
        return p;
      };
      wrapped.__sdpWrapped = true;
      window.fetch = wrapped;
    }

    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;
    if (!open.__sdpWrapped) {
      var openWrapped = function (m, u) { this.__sdpCartMutation = isMutation(u); return open.apply(this, arguments); };
      openWrapped.__sdpWrapped = true;
      XMLHttpRequest.prototype.open = openWrapped;
      XMLHttpRequest.prototype.send = function () {
        if (this.__sdpCartMutation) {
          this.addEventListener('loadend', function () { setTimeout(syncCart, 250); });
        }
        return send.apply(this, arguments);
      };
    }

    /* Coming back to a bfcached page, or back from checkout, both leave a stale badge. */
    window.addEventListener('pageshow', function (e) { if (e.persisted) syncCart(); });
    document.addEventListener('visibilitychange', function () { if (!document.hidden) syncCart(); });

    /* Apps and sections that announce themselves properly. */
    ['cart:updated', 'cart:refresh', 'slidecart:updated', 'ajaxCart:afterCartLoad'].forEach(function (evt) {
      document.addEventListener(evt, function () { syncCart(); });
    });

    /* One authoritative read on load, so a server-rendered badge that is already stale
       (bfcache, a cart emptied in another tab) is corrected once - and only once. */
    syncCart();
  }

  /* ------------------------------------------------------------------
     BACK CONTROL
     Upgrades [data-sd-back] from a parent link into a real history step, but only
     when there IS same-origin history to step back to. See snippets/sd-back.liquid.
  ------------------------------------------------------------------ */
  function initBack() {
    var links = $$('[data-sd-back]');
    if (!links.length) return;

    var ref = document.referrer;
    var sameSite = false;
    try {
      sameSite = !!ref && new URL(ref).origin === window.location.origin
                 && new URL(ref).href !== window.location.href;
    } catch (e) { sameSite = false; }

    if (!sameSite || history.length <= 1) return;   /* leave the real href alone */

    links.forEach(function (a) {
      var label = a.querySelector('[data-sd-back-text]');
      if (label) label.textContent = 'Back';
      a.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;  /* let new-tab work */
        e.preventDefault();
        history.back();
      });
    });
  }

  window.SDP = window.SDP || {};
  window.SDP.syncCart = syncCart;
  window.SDP.toast = toast;

  function boot() {
    initHeader();
    initQuickAdd();
    initTabs();
    initRails();
    initRecentlyViewed();
    initQty();
    initAutoSubmit();
    initCartWatch();
    initBack();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();